import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authApi } from "../api.js";
import { AuthLayout, Button, ErrorBanner, Field, Select, TextInput } from "../components/ui.js";

export function RegisterPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", displayName: "", tenantName: "", countryCode: "NZ" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (form.password.length < 12) {
      setError("Password must be at least 12 characters");
      return;
    }
    setBusy(true);
    const res = await authApi.register(form);
    setBusy(false);
    if (!res.ok) {
      setError(res.error?.details?.join("; ") ?? res.error?.errorCode ?? "Registration failed");
      return;
    }
    navigate(`/verify-email?email=${encodeURIComponent(form.email)}`);
  };

  return (
    <AuthLayout
      eyebrow="Start with clarity"
      title="Bring your rental property finances into one considered workspace."
      description="Create a secure account, add your first property and build a clearer picture from day one."
    >
      <div className="auth-card-heading">
        <p className="eyebrow">Get started</p>
        <h2>Create your account</h2>
        <p>Free to start. Add your portfolio details after verification.</p>
      </div>
      <form onSubmit={onSubmit} className="form-stack">
        {error && <ErrorBanner message={error} />}
        <Field label="Email address">
          <TextInput type="email" required value={form.email} onChange={set("email")} autoComplete="email" inputMode="email" placeholder="you@example.com" />
        </Field>
        <Field label="Password">
          <div className="password-field">
            <TextInput
              type={showPassword ? "text" : "password"}
              required
              minLength={12}
              value={form.password}
              onChange={set("password")}
              autoComplete="new-password"
              placeholder="At least 12 characters"
            />
            <button type="button" onClick={() => setShowPassword((current) => !current)}>
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </Field>
        <Field label="Your name">
          <TextInput required value={form.displayName} onChange={set("displayName")} autoComplete="name" placeholder="Your full name" />
        </Field>
        <Field label="Business / portfolio name">
          <TextInput required value={form.tenantName} onChange={set("tenantName")} autoComplete="organization" placeholder="For example, Harbour Rentals" />
        </Field>
        <Field label="Country">
          <Select required value={form.countryCode} onChange={set("countryCode")}>
            <option value="NZ">New Zealand</option>
          </Select>
        </Field>
        <Button type="submit" disabled={busy} className="btn-block">{busy ? "Creating your account…" : "Create account"}</Button>
        <p className="form-hint form-hint-centered">
          Already have an account? <Link to="/login">Log in</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
